/**
 * PhaseNavigation - Horizontal breadcrumb phase navigation
 * Shows phase pills with progress indicators
 */

import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { JourneyPhaseWithMode } from '@ispot/shared'

interface PhaseNavigationProps {
  phases: JourneyPhaseWithMode[]
  currentPhaseId: string
  phaseProgress: Record<string, number>
  onPhaseSelect: (phaseId: string) => void
}

export function PhaseNavigation({
  phases,
  currentPhaseId,
  phaseProgress,
  onPhaseSelect,
}: PhaseNavigationProps) {
  return (
    <nav className="flex items-center gap-2 px-8 py-4 bg-white border-b border-border-subtle overflow-x-auto">
      {phases.map((phase, index) => {
        const isActive = phase.id === currentPhaseId
        const progress = phaseProgress[phase.id] || 0
        const isCompleted = progress >= 100
        const isLocked = index > 0 && (phaseProgress[phases[index - 1].id] || 0) < 60

        return (
          <div key={phase.id} className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => !isLocked && onPhaseSelect(phase.id)}
              disabled={isLocked}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all',
                isActive && 'text-white',
                !isActive && isCompleted && 'bg-warm-100 text-warm-700',
                !isActive && !isCompleted && !isLocked && 'bg-warm-50 text-warm-600 hover:bg-warm-100',
                isLocked && 'bg-warm-50 text-text-muted cursor-not-allowed opacity-60'
              )}
              style={isActive ? { backgroundColor: phase.color || '#5c8a6b' } : undefined}
            >
              <PhaseNumber
                index={index + 1}
                isActive={isActive}
                isCompleted={isCompleted}
                isLocked={isLocked}
                color={phase.color}
              />
              <span className="whitespace-nowrap">{phase.name}</span>
            </button>

            {index < phases.length - 1 && (
              <ChevronRight className="w-4 h-4 text-warm-300 flex-shrink-0" />
            )}
          </div>
        )
      })}

      {/* Hint text */}
      <span className="ml-auto text-xs text-text-muted whitespace-nowrap">
        Complete the intake to unlock phases
      </span>
    </nav>
  )
}

function PhaseNumber({
  index,
  isActive,
  isCompleted,
  isLocked,
  color,
}: {
  index: number
  isActive: boolean
  isCompleted: boolean
  isLocked: boolean
  color?: string
}) {
  return (
    <span
      className={cn(
        'w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold',
        isActive && 'bg-white/20',
        !isActive && isCompleted && 'text-white',
        !isActive && !isCompleted && !isLocked && 'bg-warm-200',
        isLocked && 'bg-warm-200'
      )}
      style={!isActive && isCompleted ? { backgroundColor: color || '#5c8a6b' } : undefined}
    >
      {isCompleted && !isActive ? (
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        index
      )}
    </span>
  )
}
